import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty, isEqual, map, pickBy, startCase } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FlagCountry from 'src/components/common/FlagCountry';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import TooltipExplainNoTradeValue from 'src/components/common/TooltipExplainNoTradeValue';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import CustomTable from 'src/components/Table';
import CompanyTradeDataWhiteBox from 'src/features/CompanyTradeData/common/CompanyTradeDataWhiteBox';
import FilterChart from 'src/features/CompanyTradeData/common/FilterChart';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryCompanyTradeDataBOLS } from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import {
  FORMAT_DATE_TIME,
  LIMIT_GET_LIST_BILLS_OF_LADING,
  WITHHELD_GOVERNMENT_COMPANY_NAME,
} from 'src/utils/constants/config';
import { FilterWithheld, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';
import IconFeather from 'src/components/common/Icon';
import { DownloadCloud, Info } from 'react-feather';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import { useFormSearch } from 'src/zustand/useFormSearch';
import PopupExportBOLs from 'src/features/TradeData/TradeData/PopupExportBOLs';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';

const CompanyTradeDataBOL = () => {
  const { t } = useTranslation();
  const { setSort, billsOfLadingListSortParams } = useTradeDataSortStore();
  const [, setSearchParams] = useSearchParams();
  const queryParams = useQueryParams();
  const { handleOpenSearch } = useFormSearch((state) => state);
  const [openExport, setOpenExport] = useState(false);
  const bolRequestPayload = useMemo(() => {
    return pickBy(
      {
        ...queryParams,
        hs_code: queryParams.hs_code,
        keyword: queryParams.keyword,
        start_date: queryParams.start_date,
        end_date: queryParams.end_date,
        exp_country_code: queryParams.exp_country_code,
        imp_country_code: queryParams.imp_country_code,
        exp_port_code: queryParams.exp_port_code,
        imp_port_code: queryParams.imp_port_code,
        supplier_id: queryParams.supplier_id,
        buyer_id: queryParams.buyer_id,
        offset: queryParams.offset,
        limit: queryParams.limit || LIMIT_GET_LIST_BILLS_OF_LADING,
        sort_by: billsOfLadingListSortParams?.sortBy,
        order: billsOfLadingListSortParams?.sort,
        hide_withheld_government: queryParams?.hide_withheld_government || FilterWithheld.NOT_ALLOWED,
      },
      (value) => value !== undefined && value !== null && value !== '',
    );
  }, [queryParams]);

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const { isFetching, data } = useQueryCompanyTradeDataBOLS(bolRequestPayload);

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: data,
    featureInfo: {
      featureId: LIST_FEATURES.COMPANY_PROFILE_BILL_OF_LADING,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const navigate = useNavigate();

  const redirectBolDetail = (item: ICompanyTradeData) => {
    navigate(AuthRouteKeys.BillOfLadingDetail.replace(':id?', item?.id));
  };

  const configColumn: ConfigCell<ICompanyTradeData>[] = [
    {
      disablePadding: false,
      disableSort: true,
      id: 'bol',
      label: 'Bill of Lading',
      onClick: redirectBolDetail,
      cell: (data: ICompanyTradeData) => {
        return (
          <Typography variant="body2" color="secondary">
            {renderTextOrDefault(data?.bol)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'actual_arrival_date',
      label: 'Date',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color="text.secondary">
          {dayjs(data?.actual_arrival_date?.toString()).format(FORMAT_DATE_TIME.DD_MM_YYYY)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'buyer',
      disableSort: true,
      label: 'Buyer',
      cell: (data: ICompanyTradeData) => {
        // <Typography variant="body2" color="secondary.main">
        //   <FlagCountry width={24} code={data.buyer_country_code} /> {renderTextOrDefault(data?.buyer_name)}
        // </Typography>
        //
        const isWithheldByGovernment = isEqual(data.buyer_name, WITHHELD_GOVERNMENT_COMPANY_NAME);
        return (
          <Stack alignItems={'center'} direction="row" spacing={0.5}>
            {isWithheldByGovernment ? (
              <IconFeather muiProps={{ fontSize: 'small', color: 'action' }} icon={<Info />} />
            ) : (
              <FlagCountry code={data?.buyer_country_code || ''} />
            )}
            <LinkToCompanyDetail
              id={data?.buyer_id}
              companyName={renderTextOrDefault(data?.buyer_name) as string}
              params={bolRequestPayload}
              viewedAsType={TypeBuyerSupplier.buyer}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'supplier',
      disableSort: true,
      label: 'Supplier',
      cell: (data: ICompanyTradeData) => {
        // <Typography variant="body2" color="secondary.main">
        //   <FlagCountry width={24} code={data.buyer_country_code} /> {renderTextOrDefault(data?.buyer_name)}
        // </Typography>
        //
        const isWithheldByGovernment = isEqual(data.supplier_name, WITHHELD_GOVERNMENT_COMPANY_NAME);
        return (
          <Stack alignItems={'center'} direction="row" spacing={0.5}>
            {isWithheldByGovernment ? (
              <IconFeather muiProps={{ fontSize: 'small', color: 'action' }} icon={<Info />} />
            ) : (
              <FlagCountry code={data?.supplier_country_code || ''} />
            )}
            <LinkToCompanyDetail
              id={data?.supplier_id}
              companyName={renderTextOrDefault(data?.supplier_name) as string}
              params={bolRequestPayload}
              viewedAsType={TypeBuyerSupplier.supplier}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'hscode',
      disableSort: true,
      label: 'HS Code',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(data?.hs_code)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: 'Weight (KG)',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data?.weight)}
          {isEqual(data?.weight, 0) && <TooltipExplainNoTradeValue />}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: 'Quantity',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data?.quantity)}
          {isEqual(data?.quantity, 0) && <TooltipExplainNoTradeValue />}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: 'Values USD',
      cell: (data: ICompanyTradeData) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data?.value_usd)}
        </Typography>
      ),
    },
  ];

  if (!shouldShowBillableDataLoading && !isFetching && isEmpty(getArrayDefault(data?.data))) {
    return (
      <CompanyInfoNoData
        title={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
        content={
          hasEnoughCredit ? (
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      />
    );
  }

  return (
    <CompanyTradeDataWhiteBox>
      <PopupExportBOLs
        type={'company'}
        open={openExport}
        handleClose={() => setOpenExport(false)}
        totalRecordsAvailable={data?.total_records || 0}
      />
      <Stack spacing={2} alignItems="center" flexWrap={'wrap'} justifyContent={'space-between'} flexDirection={'row'}>
        <Typography textAlign={'left'} variant="h5">
          Most Recent Bills Of Lading
        </Typography>
        <Stack direction="row" spacing={2} alignItems={'flex-start'}>
          <TextField
            sx={{ maxWidth: 400 }}
            disabled={isFetching}
            onChange={(e) => {
              setSearchParams((searchParams) => {
                searchParams.set('hide_withheld_government', String(e.target.value));
                return searchParams;
              });
            }}
            helperText={
              <Typography variant="caption" color="text.secondary">
                What is Withheld by Government?
                <TooltipTradeDataLimit icon={<HelpOutlineIcon color="action" sx={{ fontSize: 16 }} />} />
              </Typography>
            }
            value={queryParams?.hide_withheld_government || FilterWithheld.NOT_ALLOWED}
            defaultValue={FilterWithheld.NOT_ALLOWED}
            select
            size="small"
            color="primary"
          >
            {map(Object.values(FilterWithheld), (option) => (
              <MenuItem key={option} value={option}>
                {startCase(t(`trade_data.transactions.filter_withheld.${option}`))}
              </MenuItem>
            ))}
          </TextField>
          <Button
            onClick={() => {
              setOpenExport(true);
            }}
            sx={{
              height: 'fit-content',
              padding: '8px 10px',
            }}
            color="primary"
            size="small"
            startIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<DownloadCloud />} />}
            variant="outlined"
          >
            Export
          </Button>
          {/*<Button*/}
          {/*  onClick={() => {*/}
          {/*    handleOpenSearch(FormType.SearchTradeData);*/}
          {/*  }}*/}
          {/*  sx={{*/}
          {/*    height: 'fit-content',*/}
          {/*    padding: '8px 10px',*/}
          {/*  }}*/}
          {/*  color="primary"*/}
          {/*  startIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Sliders />} />}*/}
          {/*  variant="outlined"*/}
          {/*>*/}
          {/*  Advanced Search*/}
          {/*</Button>*/}
          {/*<FilterChart*/}
          {/*  selectedRangeTime={selectedRangeTime}*/}
          {/*  onSelectTimeRange={({ fromTime, toTime }) => {*/}
          {/*    setSelectedRangeTime({*/}
          {/*      fromTime: fromTime,*/}
          {/*      toTime: toTime,*/}
          {/*    });*/}
          {/*  }}*/}
          {/*/>*/}
        </Stack>
      </Stack>

      <CustomTable
        maxRow={100}
        rowsPerPageOptions={[10, 50, 100]}
        handleSort={(data) => {
          setSort('billsOfLadingListSortParams', data);
        }}
        onChangePage={(value) => {
          setSearchParams((searchParams) => {
            searchParams.set('offset', String(value));
            return searchParams;
          });
        }}
        onChangePageSize={(value) =>
          setSearchParams((searchParams) => {
            searchParams.set('offset', '0');
            searchParams.set('limit', String(value));
            return searchParams;
          })
        }
        // onRowClick={redirectBolDetail}
        loading={shouldShowBillableDataLoading || isFetching}
        page={parseInt(queryParams.offset || 0)}
        pageSize={parseInt(queryParams.limit || LIMIT_GET_LIST_BILLS_OF_LADING)}
        totalRecords={data?.total_records}
        dataKey={'id'}
        data={data?.data ?? []}
        configColumn={configColumn}
      />
    </CompanyTradeDataWhiteBox>
  );
};

export default CompanyTradeDataBOL;
